/**
 * Platform-specific cancellation / expiration reason codes, with human copy and a
 * longer explainer shown on hover. The SAME integer means different things on
 * Android vs iOS, which is why this is keyed by platform.
 *
 * Sources:
 * - Android: Google Play Developer API — SubscriptionPurchase.cancelReason
 *   (0 user, 1 system/billing, 2 replaced, 3 developer).
 * - iOS: Apple App Store Server API — expiration_intent / expirationIntent
 *   (1 customer canceled, 2 billing error, 3 declined price increase,
 *    4 product unavailable).
 */

export type ReceiptPlatform = "ios" | "android" | "web" | "unknown";

export interface CancelReasonDoc {
    /** Short label for the badge/cell. */
    label: string;
    /** Longer explainer surfaced on hover. */
    explainer: string;
    /** Visual tone — "neutral" for benign, "warn" for involuntary/billing. */
    tone: "neutral" | "warn" | "info";
}

const ANDROID_CANCEL_REASONS: Record<number, CancelReasonDoc> = {
    0: {
        label: "User canceled",
        explainer:
            "The user voluntarily canceled the subscription in the Google Play store. Voluntary churn.",
        tone: "neutral",
    },
    1: {
        label: "Billing / system issue",
        explainer:
            "The subscription was canceled by the system, usually because of a billing problem (e.g. the payment method was declined). Involuntary churn — a good win-back target.",
        tone: "warn",
    },
    2: {
        label: "Replaced",
        explainer:
            "The subscription was replaced by a new subscription (e.g. an upgrade/downgrade or plan change). Not a true cancellation.",
        tone: "info",
    },
    3: {
        label: "Developer canceled",
        explainer:
            "The subscription was canceled by the developer (us) via the Play Developer API — e.g. a refund or manual revoke.",
        tone: "info",
    },
};

const IOS_CANCEL_REASONS: Record<number, CancelReasonDoc> = {
    1: {
        label: "Customer canceled",
        explainer:
            "The customer voluntarily turned off auto-renew in their Apple subscription settings. Voluntary churn.",
        tone: "neutral",
    },
    2: {
        label: "Billing error",
        explainer:
            "Apple could not charge the customer's payment method (billing error). Involuntary churn — a good win-back / payment-recovery target.",
        tone: "warn",
    },
    3: {
        label: "Declined price increase",
        explainer:
            "The customer did not consent to an auto-renewable subscription price increase, so the subscription lapsed.",
        tone: "warn",
    },
    4: {
        label: "Product unavailable",
        explainer:
            "The product was not available for purchase at renewal time, so the subscription did not renew.",
        tone: "info",
    },
};

/**
 * Resolve a store cancel/expiry reason code to its documented meaning for the
 * given platform. Returns null when there is no reason (active sub) or the code
 * is unknown for that platform.
 */
export function getCancelReasonDoc(
    platform: ReceiptPlatform,
    code: number | null | undefined
): CancelReasonDoc | null {
    if (code == null) return null;
    const table =
        platform === "android"
            ? ANDROID_CANCEL_REASONS
            : platform === "ios"
            ? IOS_CANCEL_REASONS
            : null;
    return table?.[code] ?? null;
}
